import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { getPlayerFilters } from 'redux/selectors/backoffice/player-report-selectors';
import { initFiltersFromURL } from 'redux/actions/backoffice/player-report-actions';
import { IPlayerFilters } from 'redux/reducers/backoffice/player-report-reducer';

export const useURLSync = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const filters = useSelector(getPlayerFilters);
    const lastSyncedUrlRef = useRef<string | null>(null);
    const isInitialMountRef = useRef<boolean>(true);
    const [urlSyncComplete, setUrlSyncComplete] = useState<boolean>(false);
    
    useEffect(() => {
        const wasInitialMount = isInitialMountRef.current;
        
        if (wasInitialMount) {
            isInitialMountRef.current = false;
        } else {
            if (location.search === lastSyncedUrlRef.current) {
                return;
            }
        }
        
        lastSyncedUrlRef.current = location.search;
        
        const searchParams = new URLSearchParams(location.search);
        const urlRoundId = searchParams.get('roundId');
        const urlFromDate = searchParams.get('fromDate');
        const urlToDate = searchParams.get('toDate');
        const urlRoundTypes = searchParams.get('roundTypes');
        const urlPage = searchParams.get('page');
        const urlPageSize = searchParams.get('pageSize');
        
        const urlFilters: Partial<IPlayerFilters> = {};
        let hasChanges = false;
        
        if (urlRoundId !== filters.roundId) {
            urlFilters.roundId = urlRoundId;
            hasChanges = true;
        }

        const shouldOmitDates = !!(urlRoundId && urlRoundId.trim().length > 0);
        
        if (!shouldOmitDates && urlFromDate) {
            const parsedDate = dayjs(urlFromDate);
            if (!filters.fromDate || !parsedDate.isSame(filters.fromDate, 'day')) {
                urlFilters.fromDate = parsedDate;
                hasChanges = true;
            }
        } else if (shouldOmitDates && filters.fromDate) {
            urlFilters.fromDate = null;
            hasChanges = true;
        }
        
        if (!shouldOmitDates && urlToDate) {
            const parsedDate = dayjs(urlToDate);
            if (!filters.toDate || !parsedDate.isSame(filters.toDate, 'day')) {
                urlFilters.toDate = parsedDate;
                hasChanges = true;
            }
        } else if (shouldOmitDates && filters.toDate) {
            urlFilters.toDate = null;
            hasChanges = true;
        }
        
        if (urlRoundTypes) {
            const types = urlRoundTypes.split(',').filter(Boolean);
            if (JSON.stringify(types) !== JSON.stringify(filters.roundTypes)) {
                urlFilters.roundTypes = types;
                hasChanges = true;
            }
        }
        
        if (urlPage) {
            const page = Number(urlPage);
            if (page !== filters.currentPage) {
                urlFilters.currentPage = page;
                hasChanges = true;
            }
        }
        
        if (urlPageSize) {
            const pageSize = Number(urlPageSize);
            if (pageSize !== filters.pageSize) {
                urlFilters.pageSize = pageSize;
                hasChanges = true;
            }
        }
        
        if (hasChanges) {
            dispatch(initFiltersFromURL(urlFilters));
            setUrlSyncComplete(true);
        } else {
            setUrlSyncComplete(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, dispatch]);
    
    useEffect(() => {
        if (!location.search) {
            setUrlSyncComplete(true);
        }
    }, [location.search]);
    
    // Sync FROM state to URL - when filters change
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        let urlChanged = false;
        
        if (location.search !== lastSyncedUrlRef.current) {
            return;
        }
        
        if (filters.roundId && filters.roundId.trim()) {
            if (searchParams.get('roundId') !== filters.roundId) {
                searchParams.set('roundId', filters.roundId.trim());
                urlChanged = true;
            }
        } else {
            // If state doesn't have roundId, remove it from URL (user cleared the filter)
            if (searchParams.has('roundId')) {
                searchParams.delete('roundId');
                urlChanged = true;
            }
        }
        
        if (filters.fromDate && filters.toDate && !filters.roundId) {
            const fromDateStr = filters.fromDate.toISOString();
            const toDateStr = filters.toDate.toISOString();
            
            if (searchParams.get('fromDate') !== fromDateStr) {
                searchParams.set('fromDate', fromDateStr);
                urlChanged = true;
            }
            if (searchParams.get('toDate') !== toDateStr) {
                searchParams.set('toDate', toDateStr);
                urlChanged = true;
            }
        } else {
            if (searchParams.has('fromDate')) {
                searchParams.delete('fromDate');
                urlChanged = true;
            }
            if (searchParams.has('toDate')) {
                searchParams.delete('toDate');
                urlChanged = true;
            }
        }
        
        if (filters.roundTypes.length > 0) {
            const typesStr = filters.roundTypes.join(',');
            if (searchParams.get('roundTypes') !== typesStr) {
                searchParams.set('roundTypes', typesStr);
                urlChanged = true;
            }
        } else {
            if (searchParams.has('roundTypes')) {
                searchParams.delete('roundTypes');
                urlChanged = true;
            }
        }
        
        if (filters.currentPage > 1) {
            const pageStr = String(filters.currentPage);
            if (searchParams.get('page') !== pageStr) {
                searchParams.set('page', pageStr);
                urlChanged = true;
            }
        } else {
            if (searchParams.has('page')) {
                searchParams.delete('page');
                urlChanged = true;
            }
        }
        
        if (filters.pageSize !== 20) {
            const pageSizeStr = String(filters.pageSize);
            if (searchParams.get('pageSize') !== pageSizeStr) {
                searchParams.set('pageSize', pageSizeStr);
                urlChanged = true;
            }
        } else {
            if (searchParams.has('pageSize')) {
                searchParams.delete('pageSize');
                urlChanged = true;
            }
        }
        
        if (urlChanged) {
            const newSearch = searchParams.toString();
            const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
            lastSyncedUrlRef.current = newSearch ? `?${newSearch}` : '';
            navigate(newUrl, { replace: true });
        }
    }, [filters, navigate, location.pathname, location.search]);
    
    return urlSyncComplete;
};
